import React from 'react';
import styled from '@emotion/styled';
import { ColumnLayout } from '../../../../../../ui';
import { Option, OptionWrap } from './Option';
import { MoreInformation } from '../more-information';
import { IOptionGroup } from './types';
import { OptionBlank } from './OptionBlank';
import { NestedInputId } from '../nested-input/NestedInputId';
import { Fieldset } from '../../../../../shared/components/fieldset/Fieldset';

interface IOptionGroupProps<TOptionGroupId, TOptionId> {
	optionGroup: IOptionGroup<TOptionGroupId, TOptionId>;
}

export const OptionGroup = <
	TOptionGroupId extends string | number,
	TOptionId extends string | number,
>({
	optionGroup,
}: IOptionGroupProps<TOptionGroupId, TOptionId>) => {
	const {
		id,
		title,
		headline,
		moreInformation,
		hasMutuallyExclusiveOptions,
		options,
		nestedInputLevel,
	} = optionGroup;

	const type = hasMutuallyExclusiveOptions ? 'radio' : 'checkbox';

	// The 'blank' option should use the same Nested Input Level as its sibling(s)
	const blankNestedInputLevel = options[0]?.nestedInputLevel;

	if (!options.length) {
		return null;
	}

	return (
		<Wrap>
			<Fieldset>
				<legend>
					<Title>
						{title}
						{moreInformation && <MoreInformation text={moreInformation} />}
					</Title>
					{headline && <Headline>{headline}</Headline>}
				</legend>
				<NestedInputId level={nestedInputLevel} value={id} />
				<CustomColumnLayout>
					{hasMutuallyExclusiveOptions && (
						<OptionBlank nestedInputLevel={blankNestedInputLevel} />
					)}
					{options.map((option) => (
						<Option key={option.id} option={option} type={type} />
					))}
				</CustomColumnLayout>
			</Fieldset>
		</Wrap>
	);
};

const CustomColumnLayout = styled(ColumnLayout)`
	/*
		Items inside the column layout can only have spacing at the top, because spacing at the bottom will cause misalignment of the next column.
		To keep the spacing consistent we compensate the spacing at the top and bottom of the outer element.
	*/
	margin-top: -8px;
	margin-bottom: 8px;
	break-inside: avoid;

	${OptionWrap} {
		padding-top: 16px;
	}
`;

const Title = styled.h3`
	display: flex;
	align-items: center;
	margin-bottom: 0;
`;

const Headline = styled.p`
	margin-bottom: 0;
`;

const Wrap = styled.div`
	padding: 24px;

	&:first-of-type {
		margin-top: 80px;

		@media (max-width: 1248px) {
			margin-top: 0px;
		}
	}
`;
